import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

const FN_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/sync-code`;

export function useSyncCode() {
  const [code, setCode] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const fetchOrCreateCode = useCallback(async () => {
    setCodeLoading(true);
    setCodeError(null);
    try {
      // Retry getSession() in case the anonymous sign-in just completed and
      // the session hasn't propagated to storage yet (common race on fresh page loads).
      let session = null;
      const delays = [0, 200, 400, 800, 1500, 2500];
      for (const delay of delays) {
        if (delay > 0) {
          await new Promise<void>((r) => setTimeout(r, delay));
        }
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          session = data.session;
          break;
        }
      }

      if (!session) {
        // Last resort: anonymous sign-in may have silently failed in useAuth —
        // try it directly here so we get a specific error if it's broken.
        const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously();
        if (!anonError && anonData.session) {
          session = anonData.session;
        } else {
          const reason = anonError?.message ?? "Unknown auth error";
          const projectUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
          const projectId = projectUrl?.replace("https://", "").replace(".supabase.co", "") ?? "unknown";
          setCodeError(
            `Auth failed: ${reason} — Make sure Anonymous sign-ins are enabled in your Supabase dashboard for project "${projectId}" (Authentication → Sign In Methods → Anonymous).`
          );
          setCodeLoading(false);
          return;
        }
      }

      const resp = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "save",
          refresh_token: session.refresh_token,
        }),
      });

      if (!resp.ok) {
        const text = await resp.text();
        setCodeError(`Server error (${resp.status}): ${text.slice(0, 120)}`);
        setCodeLoading(false);
        return;
      }

      const json = await resp.json();
      if (json.error) {
        setCodeError(json.error);
      } else if (json.code) {
        setCode(json.code);
      } else {
        setCodeError("No code returned from server. Try again.");
      }
    } catch (e) {
      setCodeError(e instanceof Error ? e.message : "Network error. Check connection.");
    }
    setCodeLoading(false);
  }, []);

  const redeemCode = async (inputCode: string): Promise<{ error: string | null }> => {
    try {
      const resp = await fetch(FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "redeem", code: inputCode }),
      });
      const json = await resp.json();
      if (json.error) return { error: json.error };

      // Use the refresh_token to restore the session as the other user
      const { error } = await supabase.auth.refreshSession({
        refresh_token: json.refresh_token,
      });
      if (error) return { error: error.message };

      // After transfer, save the new session's refresh_token so the code stays fresh
      const { data: { session: newSession } } = await supabase.auth.getSession();
      if (newSession) {
        await fetch(FN_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${newSession.access_token}`,
          },
          body: JSON.stringify({
            action: "save",
            refresh_token: newSession.refresh_token,
          }),
        });
      }

      return { error: null };
    } catch {
      return { error: "Something went wrong. Please try again." };
    }
  };

  return { code, codeLoading, codeError, fetchOrCreateCode, redeemCode };
}
